/* eslint-disable @typescript-eslint/no-var-requires */
/* eslint-disable global-require */
import { useRouter } from '@application/utils';
import { Analytics } from '@application/utils/analytics';
import {
  Button,
  CardItemArticle,
  CardItemShelter,
  Container,
  DetailListContact,
  HeaderProfile,
  RelatedLinks,
  Section,
  SectionCardItemSlider,
  SectionSubtitle,
  SectionText,
  SideLayoutContentWrapper,
  SideLayoutSideWrapper,
  SideLayoutStickyWrapper,
  SideLayoutWrapper,
  SkeletonBox,
  Slideshow,
} from '@presentation/components';
import { useTranslation } from '@presentation/utils';
import type { FC } from 'react';
import { Suspense, lazy, useMemo, useState } from 'react';
import { TbShare } from 'react-icons/tb';
import type { ShelterPageProps } from '..';
import * as Styled from './styled';

export * from './types';

const Map = lazy(() =>
  import('../../components/Map').then((module) => ({ default: module.default })),
);

export const ShelterPage: FC<ShelterPageProps> = (props) => {
  const {
    shelterLoading,
    shelterData,
    slideshowData,
    articleItems = [],
    articleItemsLoading,
    relatedLinks,
    shelterItems = [],
    shelterItemsLoading,
    onSocialShareClick,
  } = props;
  const { translate } = useTranslation();
  const { translatedRoute, routeLocationDescriptor, countryCode } = useRouter();
  const [showContactInfo, setShowContactInfo] = useState(false);

  const articleCards = useMemo(() => {
    if (articleItemsLoading) {
      return Array(4)
        .fill(undefined)
        .map((_, index) => (
          <CardItemArticle style={{ height: 325 }} key={index} loading title="" />
        ));
    }

    return articleItems.map((item, index) => (
      <CardItemArticle style={{ height: 325 }} key={index} {...item} />
    ));
  }, [articleItems, articleItemsLoading]);

  const shelterCards = useMemo(() => {
    if (shelterItemsLoading) {
      return Array(4)
        .fill(undefined)
        .map((_, index) => <CardItemShelter style={{ height: 351 }} key={index} loading />);
    }

    return shelterItems.map((item, index) => (
      <CardItemShelter style={{ height: 351 }} key={index} {...item} />
    ));
  }, [shelterItems, shelterItemsLoading]);

  return (
    <>
      <HeaderProfile
        title={
          !shelterLoading ? (
            translate('shelterDetails.title').replace('$name', shelterData?.name ?? '')
          ) : (
            <SkeletonBox style={{ width: '20%', height: '30px' }} />
          )
        }
        tags={
          shelterData?.spokenLanguages
            ? shelterData?.spokenLanguages?.split(', ').map((s) => ({ content: s, theme: 'light' }))
            : []
        }
        backLink={routeLocationDescriptor('shelterList')}
      >
        <Styled.ShareWrapper>
          <Button
            shrink
            color="dark"
            iconLeft={<TbShare />}
            size="small"
            onClick={onSocialShareClick}
          >
            {translate('common.buttons.share')}
          </Button>
        </Styled.ShareWrapper>
      </HeaderProfile>

      <Container>
        <SideLayoutWrapper>
          <SideLayoutContentWrapper>
            {!!slideshowData?.items?.length && (
              <div style={{ minHeight: 400 }}>
                <Slideshow {...slideshowData} />
              </div>
            )}

            <Section>
              <SectionSubtitle>{translate('glossary.description')}</SectionSubtitle>
              <SectionText>{shelterData?.text}</SectionText>
              {shelterLoading && <SkeletonBox style={{ width: '100%', height: '100px' }} />}
              {shelterLoading && shelterData?.description && (
                <SectionText>{shelterData.description}</SectionText>
              )}

              {!shelterLoading && shelterData?.additionalInfo && (
                <SectionText>{shelterData.additionalInfo}</SectionText>
              )}
            </Section>
          </SideLayoutContentWrapper>
          <SideLayoutSideWrapper>
            <SideLayoutStickyWrapper>
              <Styled.SideContentWrapper>
                <Styled.SideContentHeader>
                  <Styled.SideContentTitle>
                    {translate('shelterDetails.contact').replace('$name', shelterData?.name ?? '')}
                  </Styled.SideContentTitle>
                </Styled.SideContentHeader>

                <DetailListContact
                  email={shelterData?.email}
                  phoneNumber={shelterData?.phoneNumber}
                  website={{
                    label: translate('glossary.shelterWebsite'),
                    url: shelterData?.website,
                  }}
                  address={{ label: shelterData?.formattedAddress }}
                  blurred={!showContactInfo}
                  blurredChildren={
                    <Button
                      color="blue"
                      shrink
                      onClick={() => {
                        setShowContactInfo(true);
                        Analytics.sendGetShelterInfosEvent();
                      }}
                      size="large"
                    >
                      {translate('common.buttons.getCoords')}
                    </Button>
                  }
                />
              </Styled.SideContentWrapper>
            </SideLayoutStickyWrapper>
          </SideLayoutSideWrapper>
        </SideLayoutWrapper>

        {!!Map && shelterData?.address?.lat && shelterData?.address?.lng && (
          <Styled.MapContainer>
            <Suspense fallback="...">
              <Map
                style={{ height: '400px' }}
                center={{ lat: shelterData?.address?.lat, lng: shelterData?.address?.lng }}
                zoom={12}
              />
            </Suspense>
          </Styled.MapContainer>
        )}

        <SectionCardItemSlider
          title={translate('shelterDetails.others.title').replace(
            '$country',
            translate(`enum.country.${countryCode}`),
          )}
          subtitle={translate('shelterDetails.others.subtitle').replace(
            '$country',
            translate(`enum.country.${countryCode}`),
          )}
          items={shelterCards}
          showMoreLink={translatedRoute('shelterList')}
        />

        <SectionCardItemSlider
          title={translate('shelterDetails.blog')}
          items={articleCards}
          showMoreLink={translatedRoute('articleList')}
        />
      </Container>

      {relatedLinks && (
        <Styled.RelatedLinksContainer>
          <RelatedLinks {...relatedLinks} />
        </Styled.RelatedLinksContainer>
      )}
    </>
  );
};
